import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { TwoFactorProvider } from './contexts/TwoFactorContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { WorkdayClientThemeProvider } from './contexts/WorkdayClientThemeContext';
import PrivateRoute from './components/PrivateRoute';
import SEOHelmet from './components/SEOHelmet';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import ProductSection from './components/ProductSection';
import Footer from './components/Footer';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Login from './pages/admin/Login';
import AdminResetPassword from './pages/admin/ResetPassword';
import Dashboard from './pages/admin/Dashboard';
import EditPages from './pages/admin/EditPages';
import Media from './pages/admin/Media';
import Contacts from './pages/admin/Contacts';
import Access from './pages/admin/Access';
import UserAccountRequests from './pages/admin/UserAccountRequests';
import WorkdayClientManagement from './pages/admin/WorkdayClientManagement';
import JobPostingsAndServices from './pages/admin/JobPostingsAndServices';
import ClientInbox from './pages/admin/ClientInbox';
import WorkdayConsultants from './pages/admin/WorkdayConsultants';
import ConsultantMessages from './pages/admin/ConsultantMessages';
import CommunityConnect from './pages/admin/CommunityConnect';
import WebsiteAnalytics from './pages/admin/WebsiteAnalytics';
import ConsultantOnboarding from './pages/admin/ConsultantOnboarding';
import TwoFactorManagement from './pages/admin/TwoFactorManagement';
import AccountSettings from './pages/admin/AccountSettings';
import ATSTesting from './pages/admin/ATSTesting';
import PaypalAudit from './pages/admin/PaypalAudit';
import ClientAuditPage from './pages/admin/ClientAudit';
import TwoFactorAudit from './pages/admin/TwoFactorAudit';
import ConsultantAuditPage from './pages/admin/ConsultantAudit';
import SecurityIncidents from './pages/admin/SecurityIncidents';
import SecurityRecovery from './pages/admin/SecurityRecovery';
import SecurityInfrastructure from './pages/admin/SecurityInfrastructure';
import LogoManagement from './pages/admin/LogoManagement';
import RequestAccess from './pages/RequestAccess';
import WorkdayServices from './pages/WorkdayServices';
import Pricing from './pages/Pricing';
import ConsultantSignup from './pages/ConsultantSignup';
import ConsultantDashboard from './pages/consultant/ConsultantDashboard';
import ConsultantSettings from './pages/consultant/ConsultantSettings';
import ConsultantLogin from './pages/consultant/ConsultantLogin';
import ConsultantResetPassword from './pages/consultant/ResetPassword';
import Opportunities from './pages/consultant/Opportunities';
import Profile from './pages/consultant/Profile';
import ViewProfile from './pages/consultant/ViewProfile';
import ConsultantProfilePublic from './pages/consultant/ConsultantProfilePublic';
import Assignments from './pages/consultant/Assignments';
import Timesheet from './pages/consultant/Timesheet';
import AIAssistant from './pages/consultant/AIAssistant';
import Resources from './pages/consultant/Resources';
import ConsultantCommunity from './pages/consultant/ConsultantCommunity';
import PerplexityALMCustomerLogin from './pages/customer/PerplexityALMCustomerLogin';
import PerplexityALMLogin from './pages/customer/PerplexityALMLogin';
import SignupConfirmation from './pages/SignupConfirmation';
import WorkdayClientDashboard from './pages/workday-client/WorkdayClientDashboard';
import WorkdayClientSettings from './pages/workday-client/WorkdayClientSettings';
import InviteTeamMember from './pages/workday-client/InviteTeamMember';
import WorkdayClientPrivacy from './pages/workday-client/WorkdayClientPrivacy';
import PostJobs from './pages/workday-client/PostJobs';
import ConsultantDocuments from './pages/workday-client/ConsultantDocuments';
import WorkdayCommunity from './pages/workday-client/WorkdayCommunity';
import PublicCommunity from './pages/PublicCommunity';
import WorkdayTalent from './pages/workday-client/WorkdayTalent';
import ContractsAndHires from './pages/workday-client/ContractsAndHires';
import ClientAIAssistant from './pages/workday-client/AIAssistant';
import ApproveTime from './pages/workday-client/ApproveTime';
import InterviewRequests from './pages/workday-client/InterviewRequests';
import ConsultantProfileView from './pages/workday-client/ConsultantProfileView';
import WorkdayClientResetPassword from './pages/workday-client/ResetPassword';
import Billing from './pages/workday-client/Billing';
import UpgradePro from './pages/workday-client/UpgradePro';
import BillingSuccess from './pages/workday-client/BillingSuccess';
import Checkout from './pages/workday-client/Checkout';
import ProClientSignup from './pages/ProClientSignup';
import ResetPassword from './pages/ResetPassword';
import BillingGateRoute from './components/BillingGateRoute';

function HomePage() {
  return (
    <div className="min-h-screen bg-background text-text relative">
      <SEOHelmet />
      <div className="bubble"></div>
      <div className="bubble"></div>
      <div className="bubble"></div>
      <Navbar />
      <Hero />
      <ProductSection />
      <Footer />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <WorkdayClientThemeProvider>
        <Router>
        <AuthProvider>
          <TwoFactorProvider>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/requestaccess" element={<RequestAccess />} />
              <Route path="/workday-services" element={<WorkdayServices />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/customer-signup" element={<ConsultantSignup />} />
              <Route path="/consultant-signup" element={<Navigate to="/customer-signup" replace />} />
              <Route path="/pro-client-signup" element={<ProClientSignup />} />
              <Route path="/workday-services-client-signup" element={<Navigate to="/pro-client-signup" replace />} />
              <Route path="/consultant/login" element={<ConsultantLogin />} />
              <Route path="/consultant/reset-password" element={<ConsultantResetPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/customer/login" element={<PerplexityALMCustomerLogin />} />
              <Route path="/perplexity-alm/login" element={<PerplexityALMLogin />} />
              <Route path="/signup-confirmation" element={<SignupConfirmation />} />
              <Route path="/consultant/dashboard" element={<PrivateRoute><ConsultantDashboard /></PrivateRoute>} />
              <Route path="/consultant/settings" element={<PrivateRoute><ConsultantSettings /></PrivateRoute>} />
              <Route path="/consultant/opportunities" element={<PrivateRoute><Opportunities /></PrivateRoute>} />
              <Route path="/consultant/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
              <Route path="/consultant/view-profile" element={<PrivateRoute><ViewProfile /></PrivateRoute>} />
              <Route path="/consultant/profile/:id" element={<PrivateRoute><ConsultantProfilePublic /></PrivateRoute>} />
              <Route path="/consultant/assignments" element={<PrivateRoute><Assignments /></PrivateRoute>} />
              <Route path="/consultant/timesheet" element={<PrivateRoute><Timesheet /></PrivateRoute>} />
              <Route path="/consultant/ai" element={<PrivateRoute><AIAssistant /></PrivateRoute>} />
              <Route path="/consultant/resources" element={<PrivateRoute><Resources /></PrivateRoute>} />
              <Route path="/consultant/community" element={<PrivateRoute><ConsultantCommunity /></PrivateRoute>} />
              <Route path="/consultant/applications" element={<Navigate to="/consultant/opportunities" replace />} />
              <Route path="/workday-client/dashboard" element={<PrivateRoute><WorkdayClientDashboard /></PrivateRoute>} />
              <Route path="/workday-client/settings" element={<PrivateRoute><WorkdayClientSettings /></PrivateRoute>} />
              <Route path="/workday-client/invite-team" element={<PrivateRoute><InviteTeamMember /></PrivateRoute>} />
              <Route path="/workday-client/privacy" element={<PrivateRoute><WorkdayClientPrivacy /></PrivateRoute>} />
              <Route path="/workday-client/jobs" element={<PrivateRoute><PostJobs /></PrivateRoute>} />
              <Route path="/workday-client/consultant-documents" element={<PrivateRoute><ConsultantDocuments /></PrivateRoute>} />
              <Route path="/workday-client/talent" element={<PrivateRoute><WorkdayTalent /></PrivateRoute>} />
              <Route path="/workday-client/contracts" element={<PrivateRoute><ContractsAndHires /></PrivateRoute>} />
              <Route path="/workday-client/ai" element={<PrivateRoute><ClientAIAssistant /></PrivateRoute>} />
              <Route path="/workday-client/timesheets" element={<PrivateRoute><ApproveTime /></PrivateRoute>} />
              <Route path="/workday-client/interview-requests" element={<PrivateRoute><InterviewRequests /></PrivateRoute>} />
              <Route path="/workday-client/consultant/:id" element={<PrivateRoute><ConsultantProfileView /></PrivateRoute>} />
              <Route path="/workday-client/billing" element={<PrivateRoute><Billing /></PrivateRoute>} />
              <Route path="/workday-client/upgrade" element={<PrivateRoute><UpgradePro /></PrivateRoute>} />
              <Route path="/workday-client/billing/success" element={<PrivateRoute><BillingSuccess /></PrivateRoute>} />
              <Route path="/workday-client/checkout" element={<BillingGateRoute><Checkout /></BillingGateRoute>} />
              <Route path="/workday-client/community" element={<WorkdayCommunity />} />
              <Route path="/community" element={<PublicCommunity />} />
              <Route path="/workday-services/client/reset-password" element={<WorkdayClientResetPassword />} />
              <Route path="/admin/login" element={<Login />} />
              <Route path="/admin/password-reset" element={<AdminResetPassword />} />
              <Route path="/admin/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
              <Route path="/admin/pages" element={<PrivateRoute><EditPages /></PrivateRoute>} />
              <Route path="/admin/media" element={<PrivateRoute><Media /></PrivateRoute>} />
              <Route path="/admin/contacts" element={<PrivateRoute><Contacts /></PrivateRoute>} />
              <Route path="/admin/workday-consultants" element={<PrivateRoute><WorkdayConsultants /></PrivateRoute>} />
              <Route path="/admin/consultant-onboarding" element={<PrivateRoute><ConsultantOnboarding /></PrivateRoute>} />
              <Route path="/admin/consultant-messages" element={<PrivateRoute><ConsultantMessages /></PrivateRoute>} />
              <Route path="/admin/community-connect" element={<PrivateRoute><CommunityConnect /></PrivateRoute>} />
              <Route path="/admin/analytics" element={<PrivateRoute><WebsiteAnalytics /></PrivateRoute>} />
              <Route path="/admin/two-factor-auth" element={<PrivateRoute><TwoFactorManagement /></PrivateRoute>} />
              <Route path="/admin/access" element={<PrivateRoute><Access /></PrivateRoute>} />
              <Route path="/admin/user-requests" element={<PrivateRoute><UserAccountRequests /></PrivateRoute>} />
              <Route path="/admin/client-inbox" element={<PrivateRoute><ClientInbox /></PrivateRoute>} />
              <Route path="/admin/workday-clients" element={<PrivateRoute><WorkdayClientManagement /></PrivateRoute>} />
              <Route path="/admin/job-postings" element={<PrivateRoute><JobPostingsAndServices /></PrivateRoute>} />
              <Route path="/admin/account-settings" element={<PrivateRoute><AccountSettings /></PrivateRoute>} />
              <Route path="/admin/ats-testing" element={<PrivateRoute><ATSTesting /></PrivateRoute>} />
              <Route path="/admin/audit/paypal" element={<PrivateRoute><PaypalAudit /></PrivateRoute>} />
              <Route path="/admin/workday/clients/audit" element={<PrivateRoute><ClientAuditPage /></PrivateRoute>} />
              <Route path="/admin/security/2fa-audit" element={<PrivateRoute><TwoFactorAudit /></PrivateRoute>} />
              <Route path="/admin/workday/consultants/audit" element={<PrivateRoute><ConsultantAuditPage /></PrivateRoute>} />
              <Route path="/admin/security/incidents" element={<PrivateRoute><SecurityIncidents /></PrivateRoute>} />
              <Route path="/admin/security/recovery" element={<PrivateRoute><SecurityRecovery /></PrivateRoute>} />
              <Route path="/admin/security/infrastructure" element={<PrivateRoute><SecurityInfrastructure /></PrivateRoute>} />
              <Route path="/admin/logo-management" element={<PrivateRoute><LogoManagement /></PrivateRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </TwoFactorProvider>
        </AuthProvider>
        </Router>
      </WorkdayClientThemeProvider>
    </ThemeProvider>
  );
}

export default App;